<template>
  <div class="searchable-location-field" ref="wrapper">
    <label v-if="label" :for="inputId" class="searchable-location-field__label">
      {{ label }}
    </label>

    <div
      class="searchable-location-field__field-block"
      :class="{ 'searchable-location-field__field-block--open': showDropdown }"
    >
      <!-- Selected value — Figma shows search icon + label + clear -->
      <div
        v-if="value && !isEditing"
        class="searchable-location-field__input searchable-location-field__input--picked"
        :class="{ 'searchable-location-field__input--disabled': disabled }"
      >
        <Icon name="interface_search" class="searchable-location-field__search-icon" />
        <span class="searchable-location-field__value">{{ value.name }}</span>
        <div
          v-if="!disabled"
          class="searchable-location-field__clear"
          role="button"
          tabindex="0"
          :aria-label="'Clear ' + label"
          @click="onClear"
          @keydown.enter.prevent="onClear"
          @keydown.space.prevent="onClear"
        >
          <Icon name="x_close_icon" />
        </div>
      </div>

      <!-- Empty / typing -->
      <div v-else ref="searchBlock" class="searchable-location-field__search">
        <ExTextInput
          ref="searchInput"
          :id="inputId"
          :value="query"
          :ghostText="placeholder"
          icon="interface_search"
          :hideFloatingLabel="true"
          :disabled="disabled"
          @input="onInput"
          @onFocus="onFocus"
        />
      </div>

      <div
        v-if="showDropdown && isEditing"
        ref="dropdownEl"
        class="searchable-location-field__dropdown"
        :style="dropdownStyle"
        role="listbox"
      >
        <LocationSearchResultsList
          :items="items"
          :loading="isTyping || loading"
          :has-query="Boolean(query)"
          :selected-id="value?.id ?? null"
          empty-message="No matches"
          @select="selectItem"
        />
      </div>
    </div>

    <div v-if="hint" class="searchable-location-field__hint">
      {{ hint }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, getCurrentInstance } from "vue";
import { useBodyPortalDropdown } from "@components/utils/useBodyPortalDropdown";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import Icon from "@components/icons/Icon.vue";
import LocationSearchResultsList from "./LocationSearchResultsList.vue";

const props = defineProps({
  value: {
    type: Object,
    default: null,
  },
  items: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Type to search",
  },
  hint: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["input", "search", "clear"]);

const instance = getCurrentInstance();
const inputId = `searchable-location-field-${instance.uid ?? instance.proxy._uid}`;

const wrapper = ref(null);
const searchInput = ref(null);
const query = ref("");
const isEditing = ref(false);
const isTyping = ref(false);
const isFocused = ref(false);
const debounceTimer = ref(null);
const searchBlock = ref(null);
const dropdownEl = ref(null);

const showDropdown = computed(
  () => isEditing.value && (isFocused.value || props.items.length > 0 || isTyping.value || props.loading)
);

const isDropdownOpen = computed(() => showDropdown.value && isEditing.value);

const { dropdownStyle, attach, detach } = useBodyPortalDropdown({
  getAnchor: () => searchBlock.value,
  getDropdown: () => dropdownEl.value,
  getContainer: () => wrapper.value,
  onClose: () => closeEditing(),
});

watch(isDropdownOpen, (open) => {
  if (open) attach();
  else detach();
});

function closeEditing() {
  isEditing.value = false;
  isFocused.value = false;
  query.value = "";
  isTyping.value = false;
}

function onFocus() {
  isEditing.value = true;
  isFocused.value = true;
  emit("search", query.value);
}

function onInput(value) {
  query.value = value;
  isEditing.value = true;
  isFocused.value = true;
  isTyping.value = true;

  if (debounceTimer.value) clearTimeout(debounceTimer.value);
  debounceTimer.value = setTimeout(() => {
    isTyping.value = false;
    emit("search", query.value);
  }, 300);
}

function selectItem(item) {
  query.value = "";
  isTyping.value = false;
  closeEditing();
  emit("input", item);
}

function onClear() {
  query.value = "";
  closeEditing();
  emit("clear");
}

onUnmounted(() => {
  clearTimeout(debounceTimer.value);
});
</script>

<style scoped>
.searchable-location-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  font-family: Inter, sans-serif;
}

.searchable-location-field__label {
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  font-weight: 500;
}

.searchable-location-field__field-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.searchable-location-field__input {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.searchable-location-field__input--picked {
  cursor: default;
}

.searchable-location-field__input--disabled {
  opacity: 0.6;
}

.searchable-location-field__search-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #667085;
}

.searchable-location-field__value {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.searchable-location-field__clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
  color: #667085;
}

.searchable-location-field__clear:hover {
  color: #344054;
}

/* XClose.vue hard-codes stroke #98A2B3, so the colour above never reached the glyph. */
.searchable-location-field__clear ::v-deep path {
  stroke: currentColor;
}

.searchable-location-field__search {
  position: relative;
  width: 100%;
}

.searchable-location-field__field-block--open .searchable-location-field__search ::v-deep .ex_text_input .v-input__slot {
  border-color: #d6bbfb !important;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.05),
    0 0 0 4px rgba(158, 119, 237, 0.24) !important;
}

.searchable-location-field__dropdown {
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow:
    0 4px 6px -2px rgba(16, 24, 40, 0.03),
    0 12px 16px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.searchable-location-field__hint {
  font-size: 12px;
  line-height: 18px;
  color: #475467;
  font-weight: 400;
}
</style>
